import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getUser } from "../../api/auth";
import { getAllOrders } from "../../api/bids";
import { orderBucket } from "../../utils/status";
import { getNotifications } from "../../api/common";
import { setUser } from "../../store/authSlice";
import Header from "./Header";
import BuyerSidebar from "./BuyerSidebar";
import Footer from "./Footer";
import MobileTabBar from "./MobileTabBar";
import useTableCardLabels from "../../hooks/useTableCardLabels";
import "./AppLayout.scss";

// Same shell as ordrx_frontend's Dashboard container: Header on top, info
// sidebar on the left (30%), routed page on the right, footer underneath.
const FULL_WIDTH_ROUTES = ["/messages"];

export default function AppLayout() {
	const dispatch = useDispatch();
	const location = useLocation();
	const summary = useBuyerSummary(location.key);
	const contentRef = useTableCardLabels();

	useEffect(() => {
		(async () => {
			const res = await getUser();
			if (res && !res.error && res.data) dispatch(setUser(res.data));
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location.pathname]);

	const fullWidth = FULL_WIDTH_ROUTES.some((p) => location.pathname.startsWith(p));

	return (
		<div className="app-shell">
			<Header unread={summary.unreadNotifications} />
			<div className={`app-body${fullWidth ? " full-width" : ""}`}>
				{!fullWidth && <BuyerSidebar summary={summary} />}
				<main className="app-content" ref={contentRef}>
					<Outlet context={{ summary }} />
				</main>
			</div>
			<Footer />
			<MobileTabBar unread={summary.unreadNotifications} />
		</div>
	);
}

// "Today's summary" counts, refreshed every minute like Seller Central's sidebar, and on
// every navigation so creating/cancelling/awarding an order shows up straight away.
function useBuyerSummary(locationKey) {
	const [summary, setSummary] = useState({ loading: true });

	useEffect(() => {
		let alive = true;
		const load = async () => {
			const [orders, notif] = await Promise.all([getAllOrders(), getNotifications()]);
			if (!alive) return;
			const list = orders && !orders.error && Array.isArray(orders.data) ? orders.data : [];
			const count = (bucket) => list.filter((o) => orderBucket(o.bidRequestState) === bucket).length;
			const notifications = notif?.notifications && !notif.notifications.error ? notif.notifications.data || [] : [];
			setSummary({
				loading: false,
				bidRequests: count("bidRequest"),
				openOrders: count("openOrder"),
				inProgress: count("ordersInProgress"),
				unreadNotifications: notifications.filter((n) => n.isRead === false).length,
			});
		};
		load();
		const timer = setInterval(load, 60 * 1000);
		return () => {
			alive = false;
			clearInterval(timer);
		};
	}, [locationKey]);

	return summary;
}
